import { TreeNode } from 'primeng/api';

import { BasicFile, FileFolder, FileProject, IFile, IFileInfo, ImageFile } from './models/file-info';
import { ProcessType } from './models/request';

export const PAGE_SIZE = 100;
export const LOAD_MORE_NODE_TYPE = 'loadmore';

export interface LoadMoreData {
  isLoadMore: true;
  parentRawData: any;
  nextPage: number;
}

export const CHEVRON_CIRCLE_DOWN_ICON_STYLE = 'pi pi-chevron-circle-down';
export const CHEVRON_CIRCLE_RIGHT_ICON_STYLE = 'pi pi-chevron-circle-right';
export const CODE_ICON_STYLE = 'pi pi-code';
export const EXCEL_ICON_STYLE = 'pi pi-file-excel';
export const FILE_ICON_STYLE = 'pi pi-file';
export const FOLDER_ICON_STYLE = 'pi pi-folder';
export const IMAGE_ICON_STYLE = 'pi pi-image';
export const IMAGES_ICON_STYLE = 'pi pi-images';
export const OPEN_FOLDER_ICON_STYLE = 'pi pi-folder-open';
export const WINDOW_MAXIMIZE_ICON_STYLE = 'pi pi-window-maximize';

// Common image extensions JIT tools typically produce as results - not
// exhaustive (the authoritative list, PreviewService.pyramidExts/
// genericImageExts, is server-side only), just enough to pick a sensible
// icon for a bare result file name with no backend round trip per row -
// see ResultSearchComponent / doc/JIT-Results.md.
const RESULT_IMAGE_EXTENSIONS = new Set([
  'tif', 'tiff', 'png', 'jpg', 'jpeg', 'ndpi', 'svs', 'bmp', 'gif',
]);

/**
 * Picks an icon for a bare file name - unlike `decorateBasicFileNode`/
 * `decorateImageFileNode` above, there's no `IFileInfo`/`supportedImage`
 * flag available (a Results-tab row is only a URI/file name flattened from
 * a run's results, not a full `FileInfo`).
 */
export function iconForFileName(fileName: string | null | undefined): string {
  const extension = (fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (RESULT_IMAGE_EXTENSIONS.has(extension)) return IMAGE_ICON_STYLE;
  if (extension === 'csv') return EXCEL_ICON_STYLE;
  if (extension === 'json' || extension === 'xml' || extension === 'txt') return CODE_ICON_STYLE;
  return FILE_ICON_STYLE;
}

export class MainUtilities {
  /** Extensions that are plain text, so get the code icon. Any other non-binary file is still tried as text. */
  public static readonly TEXT_EXTENSIONS: string[] = [
    'json', 'geojson', 'xml', 'txt', 'groovy', 'ijm', 'log', 'md', 'yaml', 'yml', 'tsv',
  ];

  /** Extensions known not to be text, which are never downloaded just to look at them. */
  public static readonly BINARY_EXTENSIONS: string[] = [
    'zip', 'gz', 'tgz', 'tar', '7z', 'bz2', 'pdf', 'h5', 'hdf5', 'zarr', 'parquet', 'npy', 'npz',
    'pkl', 'bin', 'exe', 'dll', 'jar', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'mp4', 'mov', 'avi',
  ];

  /** True when a downloaded file's content is evidently not text (NULs or replacement characters). */
  public static looksBinary(content: string): boolean {
    const sample = content.length > 8000 ? content.substring(0, 8000) : content;
    return sample.includes('\u0000') || sample.includes('\uFFFD');
  }

  public createNodes(files: IFileInfo[]): TreeNode<IFileInfo>[] {
    const nodes: TreeNode<IFileInfo>[] = [];

    files.forEach(file => {
      const node = this.createNodeFromFile(file);
      nodes.push(node);
    });

    return nodes;
  }

  public createNodeFromFile(file: IFileInfo): TreeNode {
    const node: TreeNode = this.createDefaultTreeNode(file);

    if (file instanceof FileProject) {
      this.decorateProjectNode(node);
    } else if (file instanceof ImageFile) {
      const imageFile: ImageFile = <ImageFile>file;
      this.decorateImageFileNode(imageFile, node);
    } else if (file instanceof BasicFile) {
      const basicFile: BasicFile = <BasicFile>file;
      this.decorateBasicFileNode(basicFile, node);
    } else if (file instanceof FileFolder) {
      const folder: FileFolder = <FileFolder>file;
      this.decorateFileFolder(folder, node);
    } else {
      console.error(`Invalid IFileInfo for generating node: ${JSON.stringify(file)}`);
    }

    return node;
  }

  public createLoadMoreNode(parentRawData: any, nextPage: number): TreeNode {
    return {
      label: '+',
      leaf: true,
      selectable: false,
      type: LOAD_MORE_NODE_TYPE,
      data: { isLoadMore: true, parentRawData, nextPage } as LoadMoreData
    };
  }

  private createDefaultTreeNode(file: IFileInfo): TreeNode {
    // Limit size of any preview generated from node.
    // file.height = 1024;
    return {
      data: file,
      leaf: file.type == 'File',
      label: file.name,
      children: []
    };
  }

  private decorateProjectNode(node: TreeNode) {
    node.expandedIcon = OPEN_FOLDER_ICON_STYLE;
    node.collapsedIcon = FOLDER_ICON_STYLE;
  }

  private decorateImageFileNode(imageFile: ImageFile, node: TreeNode) {
    if (imageFile.results) {
      node.icon = IMAGES_ICON_STYLE;
    } else {
      node.icon = IMAGE_ICON_STYLE;
    }
  }

  private decorateBasicFileNode(basicFile: BasicFile, node: TreeNode) {
    if (basicFile.results) {
      if (basicFile.fileExtension === 'csv') {
        node.icon = EXCEL_ICON_STYLE;
      } else {
        console.error(`Unexpeted results file type: ${JSON.stringify(basicFile)}`);
      }
    } else if (this.isText(basicFile)) {
      node.icon = CODE_ICON_STYLE;
    } else {
      node.icon = FILE_ICON_STYLE;
    }
  }

  private decorateFileFolder(folder: FileFolder, node: TreeNode) {
    if (folder.results) {
      if (folder.processType === ProcessType.CROP) {
        node.expandedIcon = WINDOW_MAXIMIZE_ICON_STYLE;
        node.collapsedIcon = WINDOW_MAXIMIZE_ICON_STYLE;
      } else if (folder.processType === ProcessType.HTQUANT) {
        node.expandedIcon = CHEVRON_CIRCLE_DOWN_ICON_STYLE;
        node.collapsedIcon = CHEVRON_CIRCLE_RIGHT_ICON_STYLE;
      } else {
        console.error(`Invalid FileInfo for generating folder node: ${JSON.stringify(folder)}`);
      }
    } else {
      node.expandedIcon = OPEN_FOLDER_ICON_STYLE;
      node.collapsedIcon = FOLDER_ICON_STYLE;
    }
  }

  public isText(file: IFile) : boolean {
    const extension: string | null = file.fileExtension;
    return extension != null && MainUtilities.TEXT_EXTENSIONS.includes(extension.toLowerCase());
  }

}
